import type { AuthMethods } from '@api/app'
import { useState } from 'react'
import { authClient } from '@web/auth'
import { useAuthMethods } from '@web/hooks'
import { describeSignInError } from '@web/router'

export const OTP_LENGTH = 6

export interface AuthFlow {
	pending: boolean
	failure: string | null
	methods: AuthMethods
	unavailable: boolean
	sendCode: (email: string) => Promise<void>
	signInWithPasskey: () => Promise<void>
}

function errorCode(error: { code?: string; status?: number; message?: string }): string {
	return error.code ?? error.message ?? String(error.status ?? 'unknown')
}

export function useAuthFlow({
	redirectTo,
	verifyTo,
	navigate,
	error,
}: {
	redirectTo: string
	verifyTo: string
	navigate: (to: string) => void
	error: string | null
}): AuthFlow {
	const [pending, setPending] = useState(false)
	const [failure, setFailure] = useState<string | null>(null)

	const { methods, unavailable } = useAuthMethods()

	function finish() {
		window.location.assign(redirectTo)
	}

	async function sendCode(email: string) {
		setFailure(null)
		setPending(true)
		try {
			const result = await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' })
			if (result.error) {
				setFailure(describeSignInError(errorCode(result.error)))
				return
			}
			const query = new URLSearchParams({ email, redirect: redirectTo })
			navigate(`${verifyTo}?${query.toString()}`)
		} finally {
			setPending(false)
		}
	}

	async function signInWithPasskey() {
		setFailure(null)
		setPending(true)
		try {
			const result = await authClient.signIn.passkey()
			if (result?.error) {
				setFailure(describeSignInError(errorCode(result.error)))
				return
			}
			finish()
		} catch (cause) {
			setFailure(describeSignInError(cause instanceof Error ? cause.name : 'unknown'))
		} finally {
			setPending(false)
		}
	}

	return {
		pending,
		failure: failure ?? (error ? describeSignInError(error) : null),
		methods,
		unavailable,
		sendCode,
		signInWithPasskey,
	}
}
